// API error types
// src/lib/types/api/error.types.ts
import { createLogger } from '$lib/utils/logger';

const logger = createLogger('api.errors');

export interface ApiErrorDetail {
	loc?: string[];
	msg: string;
	type: string;
}

export interface ApiErrorData {
	detail?: string | ApiErrorDetail[];
	message?: string;
	code?: string;
	status?: number;
	errors?: Record<string, string[]> | Array<{ field: string; message: string }>;
}

export interface ApiErrorResponse {
	status: number;
	detail?: string | ApiErrorDetail[];
	message?: string;
	code?: string;
	errors?: Record<string, string[]> | Array<{ field: string; message: string }>;
}

export class ApiError extends Error {
	status: number;
	code?: string;
	fieldErrors?: Record<string, string[]>;
	/**
	 * Raw parsed JSON body of the error response, when it was a
	 * well-formed JSON document. This is NOT structured into
	 * `message`/`fieldErrors` and is intended for endpoints whose
	 * non-2xx responses carry domain-meaningful payloads (e.g. a 409
	 * Conflict that still returns the canonical resource state).
	 *
	 * Consumers should narrow this to the expected response schema
	 * themselves. `undefined` when the body wasn't JSON-parseable.
	 */
	body?: unknown;

	constructor(
		message: string,
		status: number,
		code?: string,
		fieldErrors?: Record<string, string[]>,
		body?: unknown
	) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.code = code;
		this.fieldErrors = fieldErrors;
		this.body = body;
	}

	static fromResponse(response: Response, data?: ApiErrorResponse): ApiError {
		let errorMessage = `Error ${response.status}: ${response.statusText}`;
		let fieldErrors: Record<string, string[]> | undefined;

		if (data) {
			if (data.detail) {
				if (typeof data.detail === 'string') {
					errorMessage = data.detail;
				} else if (Array.isArray(data.detail) && data.detail.length > 0) {
					// Handle FastAPI ValidationError format
					errorMessage = data.detail.map((err) => `${err.loc?.join('.')} - ${err.msg}`).join('; ');
				}
			} else if (data.message) {
				errorMessage = data.message;
			}

			if (data.errors) {
				if (Array.isArray(data.errors)) {
					fieldErrors = {};
					data.errors.forEach((err) => {
						if (err.field && err.message) {
							fieldErrors![err.field] = fieldErrors![err.field] || [];
							fieldErrors![err.field].push(err.message);
						}
					});
				} else {
					fieldErrors = data.errors;
				}
			}
		}

		return new ApiError(errorMessage, response.status, data?.code, fieldErrors, data);
	}

	toUserFriendlyMessage(): string {
		let message = this.message;

		if (this.fieldErrors) {
			const fieldMessages = Object.entries(this.fieldErrors)
				.map(([field, messages]) => `${field}: ${messages.join(', ')}`)
				.join('; ');

			if (fieldMessages) {
				message += ` (${fieldMessages})`;
			}
		}

		return message;
	}

	hasFieldError(fieldName: string): boolean {
		return !!(this.fieldErrors && this.fieldErrors[fieldName]);
	}

	getFieldErrors(fieldName: string): string[] {
		return this.fieldErrors && this.fieldErrors[fieldName] ? this.fieldErrors[fieldName] : [];
	}

	toString(): string {
		return this.toUserFriendlyMessage();
	}
}

/**
 * Error utility functions
 */
export const ErrorUtils = {
	/**
	 * Convert unknown error to a user-friendly message
	 */
	getErrorMessage(error: unknown): string {
		if (error instanceof ApiError) {
			return error.toUserFriendlyMessage();
		} else if (error instanceof Error) {
			return error.message;
		} else if (typeof error === 'string') {
			return error;
		} else {
			return 'An unexpected error occurred';
		}
	},

	/**
	 * Process API response and throw an ApiError if it's not successful
	 * @param response The fetch Response object
	 * @returns Parsed response data of type T
	 */
	async handleApiResponse<T>(response: Response): Promise<T> {
		// Log response classification
		logger.debug('API response received', {
			url: response.url,
			status: response.status,
			statusText: response.statusText,
			ok: response.ok
		});

		if (!response.ok) {
			let errorData: ApiErrorResponse | undefined;
			let responseText: string | undefined;

			try {
				// First, get the response as text to see what we're dealing with
				responseText = await response.text();

				// Check if it's HTML (error page)
				if (responseText.startsWith('<!') || responseText.includes('<!doctype html>')) {
					logger.error('API returned HTML instead of JSON - routing issue detected', {
						url: response.url,
						status: response.status,
						possibleCauses: [
							'Incorrect API endpoint URL',
							'Backend route does not exist',
							'Proxy/routing issue'
						]
					});
					throw new Error(
						`API returned HTML instead of JSON for ${response.url}. Backend route may not exist.`
					);
				}

				// Try to parse as JSON
				errorData = JSON.parse(responseText) as ApiErrorResponse;
				logger.warn('API error response parsed', {
					url: response.url,
					status: response.status,
					code: errorData.code,
					detail: typeof errorData.detail === 'string' ? errorData.detail : 'validation_errors'
				});

				// Special handling for session expired errors
				if (response.status === 401 && errorData?.detail) {
					const detail = typeof errorData.detail === 'string' ? errorData.detail : '';
					if (
						detail.toLowerCase().includes('session expired') ||
						detail.toLowerCase().includes('invalid')
					) {
						// Clear auth data when session is expired
						if (typeof window !== 'undefined') {
							logger.info('Session expired - clearing auth data', {
								url: response.url,
								detail: detail.substring(0, 100)
							});
							localStorage.removeItem('token');
							localStorage.removeItem('refresh_token');
							localStorage.removeItem('token_expires_at');
							localStorage.removeItem('user');
							localStorage.removeItem('permissions');

							// Redirect to login if not already there
							if (window.location.pathname !== '/login') {
								import('$app/navigation').then(({ goto }) => {
									logger.info('Redirecting to login after session expiry');
									goto('/login', { replaceState: true });
								});
							}
						}
					}
				}
			} catch (e) {
				// If parsing fails, we'll use a generic error
				logger.error('Failed to parse error response', {
					url: response.url,
					status: response.status,
					error: e instanceof Error ? e.message : String(e),
					responseLength: responseText?.length
				});
			}

			throw ApiError.fromResponse(response, errorData);
		}

		// If response is OK, parse and return the data
		const responseText = await response.text();

		// Check if we got HTML instead of JSON even on success
		if (responseText.startsWith('<!') || responseText.includes('<!doctype html>')) {
			logger.error('API returned HTML on successful response', {
				url: response.url,
				status: response.status
			});
			throw new Error(`API returned HTML instead of JSON for ${response.url}`);
		}

		// Check if response is empty
		if (!responseText || responseText.trim() === '') {
			logger.warn('Empty response body received', {
				url: response.url,
				status: response.status
			});
			return {} as T; // Return empty object for empty responses
		}

		try {
			const responseData = JSON.parse(responseText);
			logger.debug('API response parsed successfully', {
				url: response.url,
				status: response.status,
				dataSize: responseText.length
			});
			return responseData as T;
		} catch (e) {
			logger.error('Failed to parse JSON response', {
				url: response.url,
				status: response.status,
				error: e instanceof Error ? e.message : String(e),
				responseLength: responseText.length
			});
			throw new Error(`Failed to parse JSON response from ${response.url}`);
		}
	},

	/**
	 * Safely handle errors in an async context
	 * @param promise The promise to handle
	 * @returns A tuple with [data, error]
	 */
	async safeAwait<T>(promise: Promise<T>): Promise<[T | null, Error | null]> {
		try {
			const data = await promise;
			return [data, null];
		} catch (error) {
			return [null, error instanceof Error ? error : new Error(String(error))];
		}
	},

	/**
	 * Format validation errors for display
	 * @param fieldErrors Record of field errors
	 * @returns Formatted string of errors
	 */
	formatValidationErrors(fieldErrors?: Record<string, string[]>): string {
		if (!fieldErrors) return '';

		return Object.entries(fieldErrors)
			.map(([field, errors]) => {
				return `${field}: ${errors.join(', ')}`;
			})
			.join('\n');
	}
};
